import * as React from "react"
import { svgIconStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { chevronStyles, PointingDirection } from "@digits-shared/components/UI/Elements/Chevron"
import { POP_UP_LIGHT_BACKGROUND_STYLES } from "@digits-shared/components/UI/Elements/PopUp/PopUp"
import { PickerPosition } from "@digits-shared/components/UI/Picker/constants"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import customKeyframes from "src/shared/config/customKeyframes"

const DIALOG_CHEVRON_SIZE = 5
const DIALOG_HORIZONTAL_OFFSET = 8
const DIALOG_MENU_WIDTH = 320

/*
 STYLES
*/

const IconContainer = styled.div`
  position: relative;
  margin-right: 4px;
`

interface DialogProps {
  pickerPosition: PickerPosition
  disabled?: boolean
  width?: number
}

export const Dialog = styled.div<DialogProps>`
  position: absolute;
  ${({ pickerPosition }) =>
    pickerPosition === PickerPosition.BelowTarget
      ? css`
          top: calc(100% + ${DIALOG_HORIZONTAL_OFFSET}px);
        `
      : css`
          bottom: calc(100% + ${DIALOG_HORIZONTAL_OFFSET}px);
        `};
  right: -${DIALOG_HORIZONTAL_OFFSET}px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 10px 0 15px;

  cursor: auto;

  ${POP_UP_LIGHT_BACKGROUND_STYLES};

  /* Show above lower rows. */
  z-index: ${zIndexes.tooltip};

  opacity: 0;
  animation: ${customKeyframes.fadeIn} 250ms ease-in forwards;

  ${({ width }) =>
    width
      ? css`
          width: ${width}px;
        `
      : css`
          max-width: ${DIALOG_MENU_WIDTH}px;
          min-width: ${DIALOG_MENU_WIDTH * 0.8}px;
        `};

  &::after {
    position: absolute;
    right: ${DIALOG_HORIZONTAL_OFFSET + DIALOG_CHEVRON_SIZE + 5}px;

    ${({ pickerPosition }) =>
      pickerPosition === PickerPosition.BelowTarget
        ? css`
            top: -${DIALOG_CHEVRON_SIZE + 1}px;
            ${chevronStyles(PointingDirection.Up, `${DIALOG_CHEVRON_SIZE}px`, colors.white)}
          `
        : css`
            bottom: -${DIALOG_CHEVRON_SIZE}px;
            ${chevronStyles(PointingDirection.Down, `${DIALOG_CHEVRON_SIZE + 1}px`, colors.white)}
          `};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: default;

      ${DialogOption} {
        opacity: 0.6;
      }
    `}
`

export const OptionTitle = styled.div`
  font-size: 12px;
  font-weight: ${fonts.weight.medium};
  flex: 1;
`

export const OptionMessage = styled.div`
  font-size: 10px;
  color: ${colors.translucentWhite70};
`

export const DialogOption = styled.div`
  display: flex;
  align-items: center;
  text-transform: none;
  user-select: none;
  color: ${colors.secondary};
  cursor: pointer;

  padding: 7px 20px 7px 12px;
  border-left: 6px solid ${colors.transparent};

  &.selected {
    &:not(:only-child) {
      border-left-color: ${colors.primary};
    }

    color: ${colors.primary};

    svg {
      ${svgIconStyles(colors.primary)};
    }
  }

  &:hover:not(.selected) {
    background-color: ${colors.white};
  }
`

export const DialogDivider = styled.hr`
  margin: 0 6px;
  border: none;
  border-top: 1px solid ${colors.translucentWhite10};
`

export const DialogIcon: React.FC<React.PropsWithChildren> = ({ children }) => (
  <IconContainer>{children}</IconContainer>
)
